import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, Star, Calculator, MessageCircle, User, Menu, X, 
  Gamepad2, Heart, LogOut, Crown, Shield,
  Sparkles, Users, BookOpen
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { GlobalSearchBar } from './GlobalSearchBar';
import { NotificationBell } from './notifications/NotificationBell';
import { getAvatarUrl } from '../utils/imageUtils';

const NAV_ITEMS = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/tarot', label: 'Tarot', icon: Star },
  { path: '/learn', label: 'Learn', icon: BookOpen },
  { path: '/calculations', label: 'Cardology', icon: Calculator },
  { path: '/games', label: 'Games', icon: Gamepad2 },
  { path: '/recruit', label: 'Recruit', icon: Users, accent: true },
];

const USER_ITEMS = [
  { path: '/profile', label: 'Profile', icon: User },
];

export const GlobalNavbar = () => {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Desktop Navigation - Fixed at top */}
      <nav className="hidden md:flex fixed top-0 left-0 right-0 z-50 bg-babylon-obsidian/95 backdrop-blur-md border-b border-babylon-gold/20">
        <div className="max-w-7xl mx-auto w-full px-4 py-2 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <Sparkles className="w-5 h-5 text-babylon-gold group-hover:rotate-12 transition-transform" />
            <span className="font-heading text-lg text-babylon-gold">CosmiCards</span>
          </Link>

          {/* Main Nav */}
          <div className="flex items-center gap-1">
            {NAV_ITEMS.map(({ path, label, icon: Icon, accent }) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${path.replace(/\//g, '') || 'home'}`}
                className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 transition-all font-medium tracking-wide ${
                  isActive(path)
                    ? (accent
                        ? 'bg-pink-500/30 text-pink-200 border border-pink-400/60 shadow-[0_0_12px_rgba(236,72,153,0.25)]'
                        : 'bg-babylon-gold/20 text-babylon-gold border border-babylon-gold/30')
                    : (accent
                        ? 'text-pink-300 hover:text-pink-200 hover:bg-pink-500/15 border border-pink-400/30'
                        : 'text-babylon-text-dim hover:text-babylon-gold hover:bg-babylon-gold/10 border border-transparent')
                }`}
              >
                <Icon size={14} />
                <span className="hidden lg:inline">{label}</span>
              </Link>
            ))}
            {/* Donate Link */}
            <a
              href="https://cash.app/$t3b3tu"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 transition-all font-medium tracking-wide text-red-400 hover:text-red-300 hover:bg-red-400/10 border border-transparent"
            >
              <Heart size={14} className="fill-current" />
              <span className="hidden lg:inline">Donate</span>
            </a>
          </div>

          {/* Search Bar */}
          {user && (
            <GlobalSearchBar compact />
          )}

          {/* User Section */}
          <div className="flex items-center gap-2">
            {user ? (
              <>
                {/* Notification Bell */}
                <NotificationBell token={token} />
                {/* Admin Panel Quick Access */}
                {(user.role === 'admin' || user.is_admin === true) && (
                  <Link
                    to="/admin"
                    className="p-1.5 rounded-lg text-yellow-400 hover:bg-yellow-400/10 transition-colors"
                    title="Admin Panel"
                  >
                    <Shield size={16} />
                  </Link>
                )}
                <Link
                  to="/profile"
                  className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-babylon-gold/10 transition-colors"
                >
                  {user.avatar_url ? (
                    <img src={getAvatarUrl(user.avatar_url)} alt="" className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-babylon-gold/20 flex items-center justify-center">
                      <User size={12} className="text-babylon-gold" />
                    </div>
                  )}
                  <span className="text-sm text-babylon-text hidden lg:inline">{user.name || 'User'}</span>
                </Link>
                <button
                  onClick={logout}
                  className="p-1.5 rounded-lg text-babylon-text-dim hover:text-red-400 hover:bg-red-400/10 transition-colors"
                  title="Logout"
                >
                  <LogOut size={16} />
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="px-3 py-1.5 rounded-lg bg-babylon-gold text-babylon-void text-sm font-medium hover:bg-babylon-gold/90 transition-colors"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation - Bottom bar + hamburger */}
      <div className="md:hidden">
        {/* Mobile Header */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-babylon-obsidian/95 backdrop-blur-md border-b border-babylon-gold/20 px-4 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-babylon-gold" />
            <span className="font-heading text-lg text-babylon-gold">CosmiCards</span>
          </Link>
          
          <div className="flex items-center gap-1">
            {user && <NotificationBell token={token} />}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg text-babylon-gold hover:bg-babylon-gold/10"
            >
              {isOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="fixed top-12 left-0 right-0 bottom-16 z-40 bg-babylon-obsidian/98 backdrop-blur-md border-b border-babylon-gold/20 shadow-xl overflow-y-auto"
            >
              <div className="p-4 space-y-2 pb-8">
                {NAV_ITEMS.map(({ path, label, icon: Icon, accent }) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`nav-mobile-${path.replace(/\//g, '') || 'home'}`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                      isActive(path)
                        ? (accent ? 'bg-pink-500/30 text-pink-200' : 'bg-babylon-gold/20 text-babylon-gold')
                        : (accent ? 'text-pink-300 hover:bg-pink-500/15' : 'text-babylon-text hover:bg-babylon-gold/10')
                    }`}
                  >
                    <Icon size={18} />
                    <span>{label}</span>
                  </Link>
                ))}
                
                {/* Donate Link */}
                <a
                  href="https://cash.app/$t3b3tu"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-red-400 hover:bg-red-400/10"
                >
                  <Heart size={18} className="fill-current" />
                  <span>Support / Donate</span>
                </a>
                
                <div className="border-t border-babylon-gold/20 pt-2 mt-2">
                  {user ? (
                    <>
                      {/* Admin Panel for Admins */}
                      {(user.role === 'admin' || user.is_admin === true) && (
                        <Link
                          to="/admin"
                          onClick={() => setIsOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                            isActive('/admin')
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'text-yellow-400 hover:bg-yellow-400/10'
                          }`}
                        >
                          <Shield size={18} />
                          <span>Admin Panel</span>
                        </Link>
                      )}
                      {USER_ITEMS.map(({ path, label, icon: Icon }) => (
                        <Link
                          key={path}
                          to={path}
                          onClick={() => setIsOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                            isActive(path)
                              ? 'bg-babylon-gold/20 text-babylon-gold'
                              : 'text-babylon-text hover:bg-babylon-gold/10'
                          }`}
                        >
                          <Icon size={18} />
                          <span>{label}</span>
                        </Link>
                      ))}
                      <button
                        onClick={() => { logout(); setIsOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-400 hover:bg-red-400/10"
                      >
                        <LogOut size={18} />
                        <span>Logout</span>
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg bg-babylon-gold/20 text-babylon-gold"
                    >
                      <User size={18} />
                      <span>Login</span>
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Bottom Tab Bar */}
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-babylon-obsidian/95 backdrop-blur-md border-t border-babylon-gold/20 safe-area-pb">
          <div className="flex items-center justify-around py-2">
            {[
              { path: '/', icon: Home, label: 'Home' },
              { path: '/tarot', icon: Star, label: 'Tarot' },
              { path: '/games', icon: Gamepad2, label: 'Games' },
              { path: '/profile', icon: User, label: 'Me' },
            ].map(({ path, icon: Icon, label }) => (
              <Link
                key={path}
                to={path}
                className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-all ${
                  isActive(path)
                    ? 'text-babylon-gold'
                    : 'text-babylon-text-dim'
                }`}
              >
                <Icon size={20} />
                <span className="text-[10px]">{label}</span>
              </Link>
            ))}
          </div>
        </nav>
      </div>

      {/* Spacer for fixed nav */}
      <div className="h-12 md:h-12" />
    </>
  );
};

export default GlobalNavbar;
